import { AutocompleteMultiSelect, AutocompleteSingleSelect } from '@shigo-ui/ui';

import { dashboardT } from '../../../../../lib/messages';
import { useChangelogLookups } from '../../../../changelog/hooks';
import type { ChangelogFilter } from '../../../../changelog/model';

interface ChangelogFiltersProps {
  filter: ChangelogFilter;
  onChange: (updates: Partial<ChangelogFilter>) => void;
}

export function ChangelogFilters({ filter, onChange }: ChangelogFiltersProps) {
  const lookups = useChangelogLookups({ enabled: true });

  return (
    <section
      aria-label={dashboardT('changelog.filters')}
      className="border-border bg-card grid min-w-0 gap-3 rounded-md border p-3 md:grid-cols-2 xl:grid-cols-6"
    >
      <AutocompleteMultiSelect
        label={dashboardT('changelog.publishers')}
        options={toOptions(lookups.publishers.data?.content ?? [])}
        value={filter.publisherIds}
        onValueChange={(publisherIds) => {
          onChange({ publisherIds });
        }}
        loading={lookups.publishers.isFetching}
        placeholder={dashboardT('changelog.publishers')}
        emptyMessage={dashboardT('changelog.lookupEmpty')}
        controlClassName="bg-card"
      />
      <AutocompleteMultiSelect
        label={dashboardT('changelog.placements')}
        options={toOptions(lookups.placements.data?.content ?? [])}
        value={filter.placementIds}
        onValueChange={(placementIds) => {
          onChange({ placementIds });
        }}
        loading={lookups.placements.isFetching}
        placeholder={dashboardT('changelog.placements')}
        emptyMessage={dashboardT('changelog.lookupEmpty')}
        controlClassName="bg-card"
      />
      <AutocompleteMultiSelect
        label={dashboardT('changelog.integrations')}
        options={toOptions(lookups.integrations.data?.content ?? [])}
        value={filter.integrationIds}
        onValueChange={(integrationIds) => {
          onChange({ integrationIds });
        }}
        loading={lookups.integrations.isFetching}
        placeholder={dashboardT('changelog.integrations')}
        emptyMessage={dashboardT('changelog.lookupEmpty')}
        controlClassName="bg-card"
      />
      <AutocompleteMultiSelect
        label={dashboardT('changelog.demandPartners')}
        options={(lookups.demandPartners.data ?? []).map((partner) => ({
          id: partner.id,
          label: partner.displayName,
        }))}
        value={filter.demandPartnerIds}
        onValueChange={(demandPartnerIds) => {
          onChange({ demandPartnerIds });
        }}
        loading={lookups.demandPartners.isFetching}
        placeholder={dashboardT('changelog.demandPartners')}
        emptyMessage={dashboardT('changelog.lookupEmpty')}
        controlClassName="bg-card"
      />
      <AutocompleteSingleSelect
        label={dashboardT('changelog.createdBy')}
        options={(lookups.users.data ?? []).map((user) => ({
          id: user.email,
          label: `${user.name} - ${user.email}`,
        }))}
        value={filter.createdBy}
        onValueChange={(createdBy) => {
          onChange({ createdBy });
        }}
        loading={lookups.users.isFetching}
        placeholder={dashboardT('changelog.createdBy')}
        searchPlaceholder={dashboardT('changelog.createdBy')}
        emptyMessage={dashboardT('changelog.lookupEmpty')}
      />
      <AutocompleteMultiSelect
        label={dashboardT('changelog.lineItems')}
        options={toOptions(lookups.lineItems.data?.content ?? [])}
        value={filter.lineItemIds}
        onValueChange={(lineItemIds) => {
          onChange({ lineItemIds });
        }}
        loading={lookups.lineItems.isFetching}
        placeholder={dashboardT('changelog.lineItems')}
        emptyMessage={dashboardT('changelog.lookupEmpty')}
        controlClassName="bg-card"
      />
      {lookups.publishers.isError ||
      lookups.placements.isError ||
      lookups.integrations.isError ||
      lookups.demandPartners.isError ||
      lookups.users.isError ||
      lookups.lineItems.isError ? (
        <p role="alert" className="text-destructive text-xs md:col-span-2 xl:col-span-6">
          {dashboardT('changelog.lookupError')}
        </p>
      ) : null}
    </section>
  );
}

function toOptions(items: Array<{ id: string; name: string }>) {
  return items.map((item) => ({ id: item.id, label: item.name }));
}
